import { useCallback, useEffect, useState } from "react";
import { useAppStore } from "../stores/appStore";
import { AudioManager, SoundEffect, PlayOptions } from "../lib/audio";

/**
 * useAudio - React hook for audio playback in components
 *
 * Provides a simple interface for playing sound effects and prompts,
 * with automatic integration with the app's mute state.
 *
 * @example
 * ```tsx
 * const { playEffect, playPrompt, isMuted, toggleMute } = useAudio();
 *
 * // Play a sound effect
 * playEffect('correct');
 *
 * // Play a voice prompt
 * await playPrompt('/audio/prompts/math/count-the-stars.mp3');
 * ```
 */
export function useAudio() {
  const { isMuted, toggleMute: storeToggleMute } = useAppStore();
  const [isInitialized, setIsInitialized] = useState(
    AudioManager.getIsInitialized()
  );

  // Sync mute state with AudioManager
  useEffect(() => {
    AudioManager.setMuted(isMuted);
  }, [isMuted]);

  // Initialize audio system on first user interaction
  useEffect(() => {
    const initializeAudio = async () => {
      if (!AudioManager.getIsInitialized()) {
        await AudioManager.initialize();
        setIsInitialized(true);
      }
    };

    // Listen for user interaction to initialize
    const handleInteraction = () => {
      initializeAudio();
      // Remove listeners after first interaction
      document.removeEventListener("click", handleInteraction);
      document.removeEventListener("touchstart", handleInteraction);
      document.removeEventListener("keydown", handleInteraction);
    };

    // If already initialized, skip
    if (AudioManager.getIsInitialized()) {
      setIsInitialized(true);
      return;
    }

    document.addEventListener("click", handleInteraction);
    document.addEventListener("touchstart", handleInteraction);
    document.addEventListener("keydown", handleInteraction);

    return () => {
      document.removeEventListener("click", handleInteraction);
      document.removeEventListener("touchstart", handleInteraction);
      document.removeEventListener("keydown", handleInteraction);
    };
  }, []);

  /**
   * Play a sound effect
   */
  const playEffect = useCallback(
    (effect: SoundEffect, options?: PlayOptions) => {
      AudioManager.playEffect(effect, options);
    },
    []
  );

  /**
   * Play a voice prompt and wait for it to complete
   */
  const playPrompt = useCallback(async (filename: string): Promise<void> => {
    return AudioManager.playPrompt(filename);
  }, []);

  /**
   * Play background music
   */
  const playMusic = useCallback((track: string) => {
    AudioManager.playMusic(track);
  }, []);

  /**
   * Stop background music
   */
  const stopMusic = useCallback(() => {
    AudioManager.stopMusic();
  }, []);

  /**
   * Stop all audio playback
   */
  const stopAll = useCallback(() => {
    AudioManager.stopAll();
  }, []);

  /**
   * Toggle mute state (syncs with app store)
   */
  const toggleMute = useCallback(() => {
    storeToggleMute();
  }, [storeToggleMute]);

  /**
   * Set master volume
   */
  const setVolume = useCallback((level: number) => {
    AudioManager.setVolume(level);
  }, []);

  /**
   * Preload sounds for a discipline
   */
  const preloadDiscipline = useCallback(async (slug: string): Promise<void> => {
    return AudioManager.preloadDiscipline(slug);
  }, []);

  return {
    // Playback methods
    playEffect,
    playPrompt,
    playMusic,
    stopMusic,
    stopAll,

    // Volume control
    isMuted,
    toggleMute,
    setVolume,

    // Preloading
    preloadDiscipline,

    // Status
    isInitialized,
  };
}
